import React, { useEffect, useState } from "react";

import Input from "src/components/Input/Input";

import "./StopWatch.scss";

interface IProps {
  id?: string,
  label?: string,
	onChange(time: string): void,
    time: string,
    hideTT?: boolean  // Option to hide milliseconds (tt)
    hideMinutes?: boolean  // Weight Pull times are under a minute
}

export const convertTimeToMinutes = (time: number) => {
  let minutes:number = Math.floor(time/60);
  let seconds:number = time - (minutes * 60);
  let thousands:number = Math.round((seconds - Math.floor(seconds)) * 100);

  return (minutes.toString().length === 1 ? "0" + minutes : minutes) + ":" + (Math.floor(seconds).toString().length === 1 ? "0" + Math.floor(seconds) : Math.floor(seconds)) + ":" + (thousands.toString().length === 1 ? "0" + thousands : thousands);
}

export const convertTimeToSeconds = (time: string) => {
  let timeSplit:string[] = time.split(':');
  let timeInSeconds:number = 0;


  if (timeSplit.length === 3) {
    if (Number(timeSplit[0]) > 0) {
      let secondsToAdd:number = Number(timeSplit[0]) * 60;
      timeInSeconds += secondsToAdd
    }

    timeInSeconds += Number(timeSplit[1]);
  }

  return Number(timeInSeconds + "." + timeSplit[2]);
}

const StopWatch: React.FC<IProps> = (props) => {
    const [seaMin, setSeaMin] = useState<string>("");
    const [seaSec, setSeaSec] = useState<string>("");
    const [seaTho, setSeaTho] = useState<string>("");
    
    // Generate unique IDs for this instance to avoid conflicts when multiple StopWatch components are on the same page
    const uniqueId = props.id || `stopwatch-${Math.random().toString(36).substring(2, 9)}`;
    const minId = `${uniqueId}-seaMin`;
    const secId = `${uniqueId}-seaSec`;
    const thoId = `${uniqueId}-seaTho`;

    useEffect(() => {
      const parts = props.time.split(":");
      setSeaMin(props.hideMinutes ? "00" : (parts[0] ?? ""));
      setSeaSec(parts[1] ?? "");
      setSeaTho(parts[2] ?? (props.hideTT ? "00" : ""));
    }, [props.time, props.hideTT, props.hideMinutes]);

    const padWithZero = (value: string): string => {
        // If the value is a single digit, pad it with a leading zero
        if (value.length === 1 && !isNaN(Number(value))) {
          return "0" + value;
        }
        return value;
    }

    const handleBlur = (field: 'min' | 'sec' | 'tho') => {
        // Pad with zero when user tabs out or loses focus
        let paddedMin = seaMin;
        let paddedSec = seaSec;
        let paddedTho = seaTho;

        if (field === 'min' && seaMin.length === 1) {
          paddedMin = padWithZero(seaMin);
          setSeaMin(paddedMin);
        } else if (field === 'sec' && seaSec.length === 1) {
          paddedSec = padWithZero(seaSec);
          setSeaSec(paddedSec);
        } else if (field === 'tho' && seaTho.length === 1) {
          paddedTho = padWithZero(seaTho);
          setSeaTho(paddedTho);
        }

        const ttValue = props.hideTT ? "00" : paddedTho;
        props.onChange(paddedMin + ":" + paddedSec + ":" + ttValue);
    }

    const goToNext = (e: any) => {
        let t:string = minId;

        let noNoKeys:string[] = [
          "Backspace",
          "Tab",
          "ShiftLeft",
          "ShiftRight",
          "ControlLeft",
          "ControlRight",
          "KeyA"
        ];

        switch(e.target.id) {
          case minId:
            t = secId;
          break;
          case secId:
            // Only go to TT if it's not hidden
            t = props.hideTT ? secId : thoId;
          break;
        }

        if (!noNoKeys.includes(e.code) && !isNaN(e.target.value) && e.target.value.length >= 2 && !props.hideTT) {
          const nextInput = document.getElementById(t);
          if (nextInput && nextInput.id !== e.target.id) {
            nextInput.focus();
            // Auto-select all text in the next input for easy overwriting
            (nextInput as HTMLInputElement).select();
          }
        }
    }

    const minutesValue = props.hideMinutes ? "00" : seaMin;
    const timeLabel = props.label || "Search Time";

    return (
    <div className="StopWatch reason qualified">
      {!props.hideMinutes && (
      <Input
        label={timeLabel + " (mm)"}
        id={minId}
        type="text"
        value={seaMin}
        onChange={(e) => {
          e.stopPropagation();
          setSeaMin(e.target.value);
          const ttValue = props.hideTT ? "00" : seaTho;
          props.onChange(e.target.value+":"+seaSec+":"+ttValue);
        }}
        onBlur={() => handleBlur('min')}
        placeholder={"mm"}
        maxLength={2}
        onKeyUp={(e:any) => { goToNext(e); }}
      />
      )}
      <Input
        label={timeLabel + " (ss)"}
        id={secId}
        type="text"
        value={seaSec}
        onChange={(e) => {
          e.stopPropagation();
          setSeaSec(e.target.value);
          const ttValue = props.hideTT ? "00" : seaTho;
          props.onChange(minutesValue+":"+e.target.value+":"+ttValue);
        }}
        onBlur={() => handleBlur('sec')}
        placeholder={"ss"}
        maxLength={2}
        onKeyUp={(e:any) => { goToNext(e); }}
      />
      {!props.hideTT && (
        <Input
          label={timeLabel + " (tt)"}
          id={thoId}
          type="text"
          value={seaTho}
          onChange={(e) => {
            e.stopPropagation();
            setSeaTho(e.target.value);
            props.onChange(minutesValue+":"+seaSec+":"+e.target.value);
          }}
          onBlur={() => handleBlur('tho')}
          placeholder={"tt"}
          maxLength={2}
        />
      )}
    </div>
    );
};

export default StopWatch;